import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { Store } from '@ngrx/store';
import { takeWhile } from 'rxjs';

import { setFundChartSortColumn } from 'ChartApp/ts/apps/fund-chart/state/reducers/sort-column/sort-column.actions';
import { setFundChartSortDirection } from 'ChartApp/ts/apps/fund-chart/state/reducers/sort-direction/sort-direction.actions';

import { ASC, DESC } from 'Angular/constants/core.constants';
import { DataInLegendOption } from 'ChartApp/ts/enums/fund-chart-common.enum';
import { FundChartRootStore } from 'ChartApp/ts/features/fund-chart-page-shared/interfaces/fund-chart-store.interface';
import { TooltipData } from 'ChartApp/ts/interfaces/fund-chart-common.interface';

/**
 * FundChartLegendComponent
 *
 * Inputs:
 * dataInLegend: which data to show in the legend (value, value and annualized value, annualized value only, or none)
 * subchartOffsets: List of offsets, example: [0, 270, 580]
 * tooltipData: List of JS Objects representing tooltipData
 */
@Component({ standalone: false, selector: 'ycn-fund-chart-legend', templateUrl: './fund-chart-legend.template.html' })
export class FundChartLegendComponent implements OnChanges, OnInit, OnDestroy {
    @Input() dataInLegend: DataInLegendOption = DataInLegendOption.VALUE;

    @Input() subchartOffsets!: number[];

    @Input() tooltipData!: TooltipData;

    showAnnualizedValue: boolean = false;

    showValue: boolean = true;

    sortColumn: string = '';

    sortDirection = '';

    ASC = ASC;

    DESC = DESC;

    isAlive = true;

    constructor(private readonly store: Store<{ reducers: FundChartRootStore }>) {}

    ngOnInit(): void {
        this.store
            .select('reducers')
            .pipe(takeWhile(() => this.isAlive))
            .subscribe(this.mapStateToComponent);
    }

    ngOnDestroy(): void {
        this.isAlive = false;
    }

    mapStateToComponent = (state: FundChartRootStore | undefined): void => {
        // This component is used on other pages in addition to the main fund chart page.
        // FundChartRootStore will not be defined outside of the main fund chart page.
        // We don't need to do anything here in that case.
        if (state?.fundChart) {
            this.sortColumn = state.fundChart.sortColumn;
            this.sortDirection = state.fundChart.sortDirection;
        }
    };

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.dataInLegend?.currentValue) {
            this.showValue =
                this.dataInLegend === DataInLegendOption.VALUE ||
                this.dataInLegend === DataInLegendOption.VALUE_AND_ANNUALIZED_VALUE;
            this.showAnnualizedValue =
                this.dataInLegend === DataInLegendOption.ANNUALIZED_VALUE ||
                this.dataInLegend === DataInLegendOption.VALUE_AND_ANNUALIZED_VALUE;
        }
    }

    setSort(sortColumn: string): void {
        const sortDirection = this.sortDirection === ASC ? DESC : ASC;
        this.store.dispatch(setFundChartSortColumn({ sortColumn }));
        this.store.dispatch(setFundChartSortDirection({ sortDirection }));
    }
}
